import { useEffect, useMemo, useState, type CSSProperties, type JSX } from "react";
import {
  filterVisibleMarionParts,
  readAnimationTargetGroups,
  resolveGroupAnimation,
  type MarionAnimationDefinition,
} from "./marionPartVisibility";

type MarionHiddenLayer = {
  id: string;
  path: string;
  for_rotate?: { min: number; max: number } | undefined;
};

type MarionManifestPart = {
  id: string;
  file: string;
  x: number;
  y: number;
  z: number;
  visible: boolean;
  group?: string | undefined;
  hidden_layers?: MarionHiddenLayer[] | undefined;
  transform?: { rotate?: number | undefined } | undefined;
};

type MarionDrawable = {
  key: string;
  testId: string;
  file: string;
  x: number;
  y: number;
  z: number;
};

function readHiddenLayers(value: unknown): MarionHiddenLayer[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const layers = value.flatMap((layer): MarionHiddenLayer[] => {
    if (!isRecord(layer)) return [];
    const id = readOptionalString(layer["id"]);
    const path = readOptionalString(layer["path"]);
    if (id === undefined || path === undefined) return [];
    const forRotateRecord = isRecord(layer["for_rotate"]) ? layer["for_rotate"] : undefined;
    const min = forRotateRecord?.["min"];
    const max = forRotateRecord?.["max"];
    return [
      {
        id,
        path,
        ...(typeof min === "number" &&
        typeof max === "number" &&
        Number.isFinite(min) &&
        Number.isFinite(max)
          ? { for_rotate: { min, max } }
          : {}),
      },
    ];
  });
  return layers.length > 0 ? layers : undefined;
}

function selectHiddenLayer(
  layers: MarionHiddenLayer[],
  rotationDegrees: number,
): MarionHiddenLayer | null {
  if (layers.length === 0) return null;

  let best: MarionHiddenLayer | null = null;
  let bestDistance = Infinity;

  for (const layer of layers) {
    if (!layer.for_rotate) {
      if (best === null) best = layer;
      continue;
    }
    const mid = (layer.for_rotate.min + layer.for_rotate.max) / 2;
    const distance = Math.abs(rotationDegrees - mid);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = layer;
    }
  }

  return best ?? layers[0] ?? null;
}

const HIDDEN_LAYER_Z_EPSILON = 0.001;

function expandPartDrawables(part: MarionManifestPart): MarionDrawable[] {
  const rotationDegrees = part.transform?.rotate ?? 0;
  const hiddenLayer =
    part.hidden_layers !== undefined && part.hidden_layers.length > 0
      ? selectHiddenLayer(part.hidden_layers, rotationDegrees)
      : null;
  const drawables: MarionDrawable[] = [];
  if (hiddenLayer !== null) {
    drawables.push({
      key: `${part.id}::hidden::${hiddenLayer.id}`,
      testId: `marion-player-part-${part.id}::hidden::${hiddenLayer.id}`,
      file: hiddenLayer.path,
      x: part.x,
      y: part.y,
      z: part.z - HIDDEN_LAYER_Z_EPSILON,
    });
  }
  drawables.push({
    key: part.id,
    testId: `marion-player-part-${part.id}`,
    file: part.file,
    x: part.x,
    y: part.y,
    z: part.z,
  });
  return drawables;
}

type MarionRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type MarionShotMask = "none" | "bottom_fade" | "circle" | "rounded";

type MarionShotDefinition = {
  texture?: string | undefined;
  crop?: MarionRect | undefined;
  placement?: MarionRect | undefined;
  mask?: MarionShotMask | undefined;
  fallback?: { shot?: string | undefined; crop?: MarionRect | undefined } | undefined;
};

type MarionVariant = {
  shots?: Record<string, MarionShotDefinition> | undefined;
  fallback?: { shot?: string | undefined; crop?: MarionRect | undefined } | undefined;
};

type MarionExpression = Record<string, string>;

type MarionManifest = {
  canvas: {
    width: number;
    height: number;
  };
  parts: MarionManifestPart[];
  animations?: Record<string, MarionAnimationDefinition> | undefined;
  expressions?: Record<string, MarionExpression> | undefined;
  variants?: Record<string, MarionVariant> | undefined;
};

export type MarionRenderMode = "auto" | "parts" | "variants";

export type MarionPlayerProps = {
  manifestUrl?: string | null | undefined;
  fallbackImageUrl?: string | null | undefined;
  fallbackImageBackupUrl?: string | null | undefined;
  variantId?: string | null | undefined;
  shot?: "standing" | "cutin" | "portrait" | (string & {}) | undefined;
  renderMode?: MarionRenderMode | undefined;
  fit?: "contain" | "cover" | undefined;
  layout?: "canvas" | "frame" | undefined;
  imageTestId?: string | undefined;
  imageClassName?: string | undefined;
  hideFallbackImageOnError?: boolean | undefined;
  onFallbackImageError?: (() => void) | undefined;
  name: string;
  active?: boolean | undefined;
};

const DEFAULT_CANVAS = { width: 768, height: 1024 } as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readPositiveNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

function readOptionalRect(value: unknown): MarionRect | undefined {
  if (!isRecord(value)) return undefined;
  const x = value["x"];
  const y = value["y"];
  const width = value["width"];
  const height = value["height"];
  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number" ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return undefined;
  }
  return { x, y, width, height };
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const normalized = readOptionalString(item);
    return normalized === undefined ? [] : [normalized];
  });
}

function readShotDefinition(value: unknown): MarionShotDefinition | undefined {
  if (!isRecord(value)) return undefined;
  const texture = value["texture"];
  const mask = value["mask"];
  const fallbackRecord = isRecord(value["fallback"]) ? value["fallback"] : undefined;
  const fallbackShot = fallbackRecord?.["shot"];
  const fallbackCrop = readOptionalRect(fallbackRecord?.["crop"]);
  return {
    ...(typeof texture === "string" && texture.length > 0 ? { texture } : {}),
    ...(readOptionalRect(value["crop"]) !== undefined
      ? { crop: readOptionalRect(value["crop"]) }
      : {}),
    ...(readOptionalRect(value["placement"]) !== undefined
      ? { placement: readOptionalRect(value["placement"]) }
      : {}),
    ...(mask === "none" || mask === "bottom_fade" || mask === "circle" || mask === "rounded"
      ? { mask }
      : {}),
    ...(fallbackRecord !== undefined
      ? {
          fallback: {
            ...(typeof fallbackShot === "string" && fallbackShot.length > 0
              ? { shot: fallbackShot }
              : {}),
            ...(fallbackCrop !== undefined ? { crop: fallbackCrop } : {}),
          },
        }
      : {}),
  };
}

function readVariants(value: unknown): Record<string, MarionVariant> | undefined {
  if (!isRecord(value)) return undefined;
  const variants = Object.entries(value).flatMap(([variantId, rawVariant]) => {
    if (!isRecord(rawVariant)) return [];
    const shotsRecord = isRecord(rawVariant["shots"]) ? rawVariant["shots"] : undefined;
    const shots =
      shotsRecord === undefined
        ? undefined
        : Object.fromEntries(
            Object.entries(shotsRecord).flatMap(([shotId, rawShot]) => {
              const shot = readShotDefinition(rawShot);
              return shot === undefined ? [] : [[shotId, shot]];
            }),
          );
    const fallbackRecord = isRecord(rawVariant["fallback"])
      ? rawVariant["fallback"]
      : undefined;
    const fallbackShot = fallbackRecord?.["shot"];
    const fallbackCrop = readOptionalRect(fallbackRecord?.["crop"]);
    return [
      [
        variantId,
        {
          ...(shots !== undefined ? { shots } : {}),
          ...(fallbackRecord !== undefined
            ? {
                fallback: {
                  ...(typeof fallbackShot === "string" && fallbackShot.length > 0
                    ? { shot: fallbackShot }
                    : {}),
                  ...(fallbackCrop !== undefined ? { crop: fallbackCrop } : {}),
                },
              }
            : {}),
        },
      ],
    ];
  });
  return variants.length > 0 ? Object.fromEntries(variants) : undefined;
}

function readAnimations(value: unknown): Record<string, MarionAnimationDefinition> | undefined {
  if (!isRecord(value)) return undefined;
  const animations = Object.entries(value).flatMap(([animationId, rawAnimation]) => {
    if (!isRecord(rawAnimation)) return [];
    const type = readOptionalString(rawAnimation["type"]);
    if (type === undefined) return [];
    const targetGroup = readOptionalString(rawAnimation["target_group"]);
    const targetGroups = readAnimationTargetGroups(rawAnimation["target_groups"]);
    const fps = readPositiveNumber(rawAnimation["fps"], 0);
    const durationSeconds = readPositiveNumber(rawAnimation["duration_seconds"], 0);
    return [
      [
        animationId,
        {
          type,
          ...(targetGroup !== undefined ? { targetGroup } : {}),
          ...(targetGroups !== undefined ? { targetGroups } : {}),
          partIds: readStringArray(rawAnimation["part_ids"]),
          ...(fps > 0 ? { fps } : {}),
          ...(typeof rawAnimation["loop"] === "boolean" ? { loop: rawAnimation["loop"] } : {}),
          ...(durationSeconds > 0 ? { durationSeconds } : {}),
        },
      ],
    ];
  });
  return animations.length > 0 ? Object.fromEntries(animations) : undefined;
}

function readExpressions(value: unknown): Record<string, MarionExpression> | undefined {
  if (!isRecord(value)) return undefined;
  const expressions = Object.entries(value).flatMap(([expressionId, rawExpression]) => {
    if (!isRecord(rawExpression)) return [];
    const groups = Object.fromEntries(
      Object.entries(rawExpression).flatMap(([groupId, rawPartId]) => {
        const partId = readOptionalString(rawPartId);
        return partId === undefined ? [] : [[groupId, partId]];
      }),
    );
    return Object.keys(groups).length > 0 ? [[expressionId, groups]] : [];
  });
  return expressions.length > 0 ? Object.fromEntries(expressions) : undefined;
}

function parseManifest(value: unknown): MarionManifest | null {
  if (!isRecord(value)) return null;

  const canvasRecord = isRecord(value["canvas"]) ? value["canvas"] : {};
  const canvas = {
    width: readPositiveNumber(canvasRecord["width"], DEFAULT_CANVAS.width),
    height: readPositiveNumber(canvasRecord["height"], DEFAULT_CANVAS.height),
  };

  const rawParts = Array.isArray(value["parts"]) ? value["parts"] : [];
  const parts = rawParts.flatMap((part): MarionManifestPart[] => {
    if (!isRecord(part)) return [];
    const id = part["id"];
    const file = part["file"];
    if (typeof id !== "string" || typeof file !== "string") return [];
    const group = readOptionalString(part["group"]);
    const hidden_layers = readHiddenLayers(part["hidden_layers"]);
    const transformRecord = isRecord(part["transform"]) ? part["transform"] : undefined;
    const rotate = transformRecord?.["rotate"];
    return [
      {
        id,
        file,
        x: typeof part["x"] === "number" ? part["x"] : 0,
        y: typeof part["y"] === "number" ? part["y"] : 0,
        z: typeof part["z"] === "number" ? part["z"] : 0,
        visible: typeof part["visible"] === "boolean" ? part["visible"] : true,
        ...(group !== undefined ? { group } : {}),
        ...(hidden_layers !== undefined ? { hidden_layers } : {}),
        ...(typeof rotate === "number" && Number.isFinite(rotate)
          ? { transform: { rotate } }
          : {}),
      },
    ];
  });
  const animations = readAnimations(value["animations"]);
  const expressions = readExpressions(value["expressions"]);
  const variants = readVariants(value["variants"]);

  return {
    canvas,
    parts,
    ...(animations !== undefined ? { animations } : {}),
    ...(expressions !== undefined ? { expressions } : {}),
    ...(variants !== undefined ? { variants } : {}),
  };
}

function resolvePartUrl(manifestUrl: string, file: string): string {
  try {
    return new URL(file, new URL(manifestUrl, window.location.origin)).toString();
  } catch {
    return file;
  }
}

function resolveVariantShot(
  manifest: MarionManifest | null,
  variantId: string | null,
  shot: string,
): MarionShotDefinition | null {
  if (manifest === null || variantId === null) return null;
  const variant = manifest.variants?.[variantId];
  if (variant === undefined) return null;
  const requestedShot = variant.shots?.[shot];
  if (requestedShot !== undefined) return requestedShot;
  const fallbackShot = variant.fallback?.shot;
  if (fallbackShot !== undefined) {
    const fallback = variant.shots?.[fallbackShot];
    if (fallback !== undefined) return fallback;
  }
  return variant.shots?.["standing"] ?? null;
}

function resolveExpression(
  manifest: MarionManifest | null,
  variantId: string | null,
): { id: string; groups: MarionExpression } | null {
  if (manifest?.expressions === undefined) return null;
  if (variantId !== null) {
    const expression = manifest.expressions[variantId];
    return expression === undefined ? null : { id: variantId, groups: expression };
  }
  const fallbackExpression =
    manifest.expressions["neutral"] ?? manifest.expressions["default"];
  if (fallbackExpression === undefined) return null;
  return {
    id: manifest.expressions["neutral"] !== undefined ? "neutral" : "default",
    groups: fallbackExpression,
  };
}

function resolveGroupAnimationFromManifest(
  manifest: MarionManifest | null,
  animationId: string,
  expectedType: string,
): MarionAnimationDefinition | null {
  return resolveGroupAnimation(manifest?.animations, animationId, expectedType);
}

function getMaskStyle(mask: MarionShotMask | undefined): CSSProperties | undefined {
  if (mask === "bottom_fade") {
    return {
      WebkitMaskImage: "linear-gradient(to bottom, black 78%, transparent 100%)",
      maskImage: "linear-gradient(to bottom, black 78%, transparent 100%)",
    };
  }
  if (mask === "circle") {
    return {
      borderRadius: "9999px",
      overflow: "hidden",
    };
  }
  if (mask === "rounded") {
    return {
      borderRadius: "0.75rem",
      overflow: "hidden",
    };
  }
  return undefined;
}

export function MarionPlayer({
  manifestUrl,
  fallbackImageUrl,
  fallbackImageBackupUrl,
  variantId,
  shot = "standing",
  renderMode = "auto",
  fit = "contain",
  layout = "canvas",
  imageTestId,
  imageClassName,
  hideFallbackImageOnError = false,
  onFallbackImageError,
  name,
  active = true,
}: MarionPlayerProps): JSX.Element | null {
  const [manifest, setManifest] = useState<MarionManifest | null>(null);
  const [failed, setFailed] = useState(false);
  const [failedShotImageUrl, setFailedShotImageUrl] = useState<string | null>(null);
  const [failedFallbackImageUrl, setFailedFallbackImageUrl] = useState<string | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [resolvedFallbackImageUrl, setResolvedFallbackImageUrl] = useState<string | null>(
    fallbackImageUrl ?? fallbackImageBackupUrl ?? null,
  );

  useEffect(() => {
    setResolvedFallbackImageUrl(fallbackImageUrl ?? fallbackImageBackupUrl ?? null);
    setFailedFallbackImageUrl(null);
  }, [fallbackImageBackupUrl, fallbackImageUrl]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = (): void => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  const normalizedVariantId =
    variantId !== undefined && variantId !== null && variantId.trim().length > 0
      ? variantId.trim()
      : null;

  useEffect(() => {
    if (manifestUrl == null || manifestUrl.length === 0) {
      setManifest(null);
      setFailed(false);
      return;
    }

    const controller = new AbortController();
    setFailed(false);
    setManifest(null);
    const requestUrl = new URL(manifestUrl, window.location.origin).toString();

    fetch(requestUrl, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Failed to load Marion manifest");
        }
        return response.json() as Promise<unknown>;
      })
      .then((json) => {
        if (controller.signal.aborted) return;
        const parsed = parseManifest(json);
        if (parsed === null || parsed.parts.length === 0) {
          setFailed(true);
          return;
        }
        setManifest(parsed);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
        setFailed(true);
      });

    return () => {
      controller.abort();
    };
  }, [manifestUrl]);

  const activeShot = useMemo(
    () => resolveVariantShot(manifest, normalizedVariantId, shot),
    [manifest, normalizedVariantId, shot],
  );
  const activeExpression = useMemo(
    () => resolveExpression(manifest, normalizedVariantId),
    [manifest, normalizedVariantId],
  );
  const talkAnimation = useMemo(
    () => resolveGroupAnimationFromManifest(manifest, "talk", "frame_sequence"),
    [manifest],
  );
  const blinkAnimation = useMemo(
    () => resolveGroupAnimationFromManifest(manifest, "blink", "event_sequence"),
    [manifest],
  );
  const shotImageUrl =
    renderMode === "parts"
      ? null
      : activeShot?.texture !== undefined && manifestUrl != null
        ? resolvePartUrl(manifestUrl, activeShot.texture)
        : null;

  const [talkFrameIndex, setTalkFrameIndex] = useState(0);
  const [blinkFrameIndex, setBlinkFrameIndex] = useState(0);

  useEffect(() => {
    setTalkFrameIndex(0);
    if (talkAnimation === null || !active || talkAnimation.partIds.length < 2) return;

    const intervalMs = Math.max(50, Math.round(1000 / (talkAnimation.fps ?? 10)));
    const intervalId = window.setInterval(() => {
      setTalkFrameIndex((current) => (current + 1) % talkAnimation.partIds.length);
    }, intervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [active, talkAnimation]);

  useEffect(() => {
    setBlinkFrameIndex(0);
    if (blinkAnimation === null) return;

    const primaryPartIds =
      blinkAnimation.partIds.length > 0
        ? blinkAnimation.partIds
        : (blinkAnimation.targetGroups?.find((entry) => entry.partIds.length > 0)?.partIds ?? []);
    if (primaryPartIds.length < 2) return;

    const timeoutIds: number[] = [];
    const durationMs = Math.max(
      80,
      Math.round((blinkAnimation.durationSeconds ?? 0.14) * 1000),
    );
    const stepMs = Math.max(40, Math.round(durationMs / primaryPartIds.length));
    const runBlink = (): void => {
      primaryPartIds.forEach((_, index) => {
        timeoutIds.push(
          window.setTimeout(() => {
            setBlinkFrameIndex(index);
          }, index * stepMs),
        );
      });
      timeoutIds.push(
        window.setTimeout(() => {
          setBlinkFrameIndex(0);
        }, durationMs + stepMs),
      );
    };
    const firstBlinkId = window.setTimeout(runBlink, 600);
    const intervalId = window.setInterval(runBlink, 3200);

    return () => {
      window.clearTimeout(firstBlinkId);
      window.clearInterval(intervalId);
      timeoutIds.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, [blinkAnimation]);

  useEffect(() => {
    setFailedShotImageUrl(null);
  }, [shotImageUrl]);

  const activeShotImageUrl =
    shotImageUrl !== null && shotImageUrl !== failedShotImageUrl ? shotImageUrl : null;
  const visibleParts = useMemo(() => {
    const filtered = filterVisibleMarionParts({
      parts: manifest?.parts ?? [],
      talkAnimation,
      blinkAnimation,
      talkFrameIndex,
      blinkFrameIndex,
      active,
      expressionGroups: activeExpression?.groups ?? null,
    });
    return filtered.sort((a, b) => a.z - b.z);
  }, [
    active,
    activeExpression,
    blinkAnimation,
    blinkFrameIndex,
    manifest,
    talkAnimation,
    talkFrameIndex,
  ]);

  const visibleDrawables = useMemo(
    () => visibleParts.flatMap((part) => expandPartDrawables(part)),
    [visibleParts],
  );

  const hasFallbackImage =
    resolvedFallbackImageUrl !== null && resolvedFallbackImageUrl.length > 0;
  const shouldRenderManifestParts =
    manifest !== null &&
    !failed &&
    activeShotImageUrl === null &&
    shot === "standing" &&
    (renderMode === "parts" ||
      normalizedVariantId === null ||
      activeShot !== null ||
      activeExpression !== null) &&
    visibleParts.length > 0;

  if (
    !shouldRenderManifestParts && activeShotImageUrl === null &&
    (!hasFallbackImage || (hideFallbackImageOnError && failedFallbackImageUrl === resolvedFallbackImageUrl))
  ) {
    return null;
  }

  const canvas = manifest?.canvas ?? DEFAULT_CANVAS;
  const rootStyle: CSSProperties = {
    ...(layout === "canvas" ? { aspectRatio: `${canvas.width} / ${canvas.height}` } : {}),
    animation: active && !prefersReducedMotion
      ? "marionIdle 2.4s ease-in-out infinite"
      : undefined,
  };
  const imageFitClass = fit === "cover" ? "object-cover" : "object-contain";
  const rootClassName =
    layout === "frame"
      ? "relative h-full w-full overflow-hidden"
      : "relative h-full max-h-full max-w-full overflow-visible";

  return (
    <div
      data-testid="marion-player"
      data-marion-active={active ? "true" : "false"}
      data-marion-expression={activeExpression?.id ?? ""}
      data-marion-variant={normalizedVariantId ?? ""}
      data-marion-shot={shot}
      className={rootClassName}
      style={rootStyle}
      aria-hidden="true"
    >
      {activeShotImageUrl !== null ? (
        <img
          data-testid={imageTestId ?? "marion-player-shot"}
          src={activeShotImageUrl}
          alt=""
          draggable={false}
          className={`h-full w-full select-none ${imageFitClass} ${imageClassName ?? ""}`}
          style={getMaskStyle(activeShot?.mask)}
          onError={() => {
            setFailedShotImageUrl(activeShotImageUrl);
          }}
        />
      ) : shouldRenderManifestParts ? (
        visibleDrawables.map((drawable) => (
          <img
            key={drawable.key}
            data-testid={drawable.testId}
            src={resolvePartUrl(manifestUrl ?? "", drawable.file)}
            alt=""
            draggable={false}
            className={`absolute h-full w-full select-none ${imageFitClass}`}
            style={{
              left: `${(drawable.x / canvas.width) * 100}%`,
              top: `${(drawable.y / canvas.height) * 100}%`,
              zIndex: drawable.z,
            }}
          />
        ))
      ) : (
        <img
          data-testid={imageTestId ?? "marion-player-fallback"}
          src={resolvedFallbackImageUrl ?? ""}
          alt=""
          draggable={false}
          className={`h-full w-full select-none ${imageFitClass} ${imageClassName ?? ""}`}
          onError={() => {
            if (
              fallbackImageBackupUrl != null &&
              fallbackImageBackupUrl.length > 0 &&
              resolvedFallbackImageUrl !== fallbackImageBackupUrl
            ) {
              setResolvedFallbackImageUrl(fallbackImageBackupUrl);
            } else if (hideFallbackImageOnError) {
              setFailedFallbackImageUrl(resolvedFallbackImageUrl);
              onFallbackImageError?.();
            }
          }}
        />
      )}
      <span className="sr-only">{name}</span>
    </div>
  );
}
